import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const forced = user?.must_reset_password === true;
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 6) return setErr("Password must be at least 6 characters");
    if (pwd !== pwd2) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      setDone(true);
      await refresh();
      setTimeout(() => nav("/app", { replace: true }), 1500);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 vb-fade-in" data-testid="change-pwd-page">
      <form onSubmit={submit} className="w-full max-w-md vb-card p-8" data-testid="change-form">
        {forced && (
          <div className="vb-pill warn mb-4 w-full" data-testid="forced-banner">
            <ShieldAlert size={14} /> Set a new password to continue
          </div>
        )}
        <h2 className="vb-heading text-2xl mb-1" style={{ color: "var(--vb-text-2)" }}>{forced ? "Welcome — secure your account" : "Change password"}</h2>
        <p className="opacity-70 mb-5 text-sm">
          {forced
            ? "Your account was created with a temporary password. Please set your own private password — only you should know it from now on."
            : "Update the password you use to sign in."}
        </p>

        {done ? (
          <div className="vb-pill ok mb-2 w-full" data-testid="change-done">✓ Password updated</div>
        ) : (
          <>
            {err && <div className="vb-pill err mb-3 w-full" data-testid="change-error">{err}</div>}
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Current / temporary password</label>
            <input data-testid="change-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="vb-input mb-3" placeholder="Enter your current password" autoComplete="current-password" />
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>New password</label>
            <input data-testid="change-new" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="vb-input mb-3" placeholder="At least 6 characters" autoComplete="new-password" />
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Confirm new password</label>
            <input data-testid="change-confirm" type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="vb-input mb-5" placeholder="Re-enter the new password" autoComplete="new-password" />
            <button type="submit" className="vb-btn w-full" disabled={busy} data-testid="change-submit">
              {busy ? "Updating…" : "Update password"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
